import { useMemo } from 'react';

import {
  FetchMaterialsSetGqlQuery,
  MaterialsSetUUID
} from '../../materialsSetsTypes';

import { FetchMaterialsSetCacheKey } from '../../materialsSetsTypes';

import { useShowQuery } from '../../../common/reactQuery/hooks/useShowQuery';

interface MaterialsSetResponse<ItemType> {
  materialsSet: ItemType | null;
}

interface MaterialsSetOptions<ItemType> {
  materialsSets?: ItemType[];
  cacheKey: FetchMaterialsSetCacheKey;
  query: FetchMaterialsSetGqlQuery;
  uuid: MaterialsSetUUID;
  options?: {
    cacheTime?: number;
    enabled?: boolean;
    enabledPlaceholder?: boolean;
  };
}

interface UseMaterialsSetItem {
  uuid: MaterialsSetUUID;
}

const itemKey = 'materialsSet';

function useMaterialsSet<ItemType extends UseMaterialsSetItem>({
  materialsSets,
  cacheKey,
  query,
  uuid,
  options = {}
}: MaterialsSetOptions<ItemType>) {
  const placeholderData = useMemo<MaterialsSetResponse<ItemType>>(() => {
    const materialsSet = materialsSets?.find(
      (materialsSet) => materialsSet.uuid === uuid
    );

    return materialsSet ? { materialsSet } : null;
  }, [materialsSets, uuid]);

  const {
    item,
    itemError,
    itemErrorData,
    itemFetched,
    itemFetching,
    itemLoading,
    itemIsPlaceholderData,
    refetchItem,
    updateItemCache
  } = useShowQuery<MaterialsSetResponse<ItemType>, ItemType>({
    cacheKey,
    itemKey,
    options,
    placeholderData,
    query,
    uuid
  });

  return {
    materialsSet: item,
    materialsSetError: itemError,
    materialsSetErrorData: itemErrorData,
    materialsSetFetched: itemFetched,
    materialsSetFetching: itemFetching,
    materialsSetIsPlaceholderData: itemIsPlaceholderData,
    materialsSetLoading: itemLoading,
    materialsSetUpdateCache: updateItemCache,
    refetchMaterialsSet: refetchItem
  };
}

export default useMaterialsSet;
